import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Report, ReportGroup } from '../../_models/reports';
import { ConfigurationService } from 'src/app/_services/configuration.service';
import { Router, Params, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { PortalApiService } from '../../_services/portal-api.service';
import * as _ from 'lodash';
import { PreferencesService } from '../_services/preferences.service';
import { ReportFilters } from '../../_models/preferences';
import { ReportFilterService } from '../_services/report-filter.service';
import { ReportFavoriteService } from '../_services/report-favorite-service';

@Component({
  selector: 'app-report-list',
  templateUrl: './report-list.component.html',
  styleUrls: ['./report-list.component.css']
})
export class ReportListComponent implements OnInit, OnDestroy {
  availableReports: ReportGroup[] = [];
  favoriteReports: ReportGroup = new ReportGroup();
  favoriteGroups: ReportGroup[] = [];
  selectedModalGroup: ReportGroup = new ReportGroup();
  isLoading: boolean = false;
  errorRetrievingReports: boolean = false;
  noReportsReceived: boolean = false;
  noFavoriteReports: boolean = false;
  errorMessage: string = "";
  interval: any;
  reportServer: string = '';
  groupView: string = 'all';
  showReportList: boolean = false;
  listDisplay: boolean = false;
  isLoadingAllReports: boolean = false;
  groupID: number;
  filters: ReportFilters = new ReportFilters;
  totalReports: ReportGroup = new ReportGroup();
  favoriteErrorMessage: string = null;

  // Subjects
  private onDestroy$ = new Subject<void>();

  constructor(
    private configurationService: ConfigurationService,
    private titleService: Title,
    private apiService: PortalApiService,
    private reportFavService: ReportFavoriteService,
    private router: Router,
    private route: ActivatedRoute,
    private preferencesService: PreferencesService,
    private reportFilterService: ReportFilterService
  ) {
    this.reportServer = this.configurationService.config.reportViewerAddress;
    this.configurationService.configData$.subscribe(res => {
      this.reportServer = res.reportViewerAddress;
    })

    this.route.queryParams.subscribe(queryParams => {
      this.listDisplay = false;
      this.showReportList = false;
      this.groupView = "all";
      this.groupID = null;
      Object.keys(queryParams).forEach(param => {
        switch (param) {
          case 'view':
            this.groupView = queryParams[param];
            break;
          case 'listDisplay':
            const displayListStyle = queryParams[param];
            if (displayListStyle == "false") {
              this.swapViews(false);
            } else {
              this.swapViews(true);
            }
            break;
          case 'groupID':
            this.groupID = +queryParams[param];
            this.showReportList = this.groupID ? true : false;
            break;
          default:
            break;
        }
      });
    })

    this.reportFavService.availableReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedReports => { this.availableReports = updatedReports });
    this.reportFavService.favoriteGroups$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedGroups => { this.favoriteGroups = updatedGroups });
    this.reportFavService.favoriteReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedReports => { this.favoriteReports = updatedReports });
    this.reportFavService.totalReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedReports => { this.totalReports = updatedReports });
    this.reportFavService.noFavoriteReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedStatus => { this.noFavoriteReports = updatedStatus });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Report List - Productioneer Portal');
    this.loadReportList();

    // TODO: Use refresh token interval from configuration settings!
    this.interval = setInterval(() => {
      /*  Reload page after 24 hrs due to expired token */
      this.refreshData();
    }, (86400000));

    this.filters = this.reportFilterService.resetFilters();
    
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  changeGroupView(viewName: string) {
    this.groupView = viewName;
    this.updateQueryParams('view', viewName);
  }

  swapViews(displayListStyle: boolean) {
    this.listDisplay = displayListStyle;
    this.preferencesService.updateCardDisplay(this.listDisplay);
    this.updateQueryParams('listDisplay', displayListStyle);
  }

  openReportGroup(reportGroupID: number) {
    this.showReportList = true;
    this.groupID = reportGroupID;
    this.updateQueryParams('groupID', reportGroupID);
  }

  updateQueryParams(paramName: string, paramValue: any) {
    const updatedParam: Params = {};
    updatedParam[paramName] = paramValue;
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: updatedParam,
        queryParamsHandling: 'merge',
        replaceUrl: false
      }
    );
  }

  clearFilters() {
    this.filters = this.reportFilterService.resetFilters();
    this.reportFilterService.clearFilterParamsFromRoute();
  }

  backToGroups() {
    this.showReportList = false;
    this.clearFilters();
  }

  loadReportList() {
    this.availableReports = []
    this.favoriteGroups = [];
    this.favoriteReports = {
      reportGroup: "Favorite Reports",
      reports: [],
      reportGroupID: 0
    };
    this.totalReports = {
      reportGroup: "Total Reports",
      reports: [],
      reportGroupID: 0
    };

    this.isLoading = true;
    this.apiService.getReports().subscribe({
      next: (data: ReportGroup[]) => {
        if (data.length == 0) {
          this.noReportsReceived = true;
        }
        else {
          data.forEach((reportGroup: ReportGroup) => {
            for (var i = 0; i < reportGroup.reports.length; i++) {
              reportGroup.reports[i].favTogglePending = false;
              reportGroup.reports[i].reportGroup = reportGroup.reportGroup;
              reportGroup.reports[i].reportGroupID = reportGroup.reportGroupID;
              this.totalReports.reports = this.totalReports.reports.concat(_.cloneDeep(reportGroup.reports[i]));
              if (reportGroup.reports[i].isFavorite) {
                this.favoriteReports.reports = this.favoriteReports.reports.concat(_.cloneDeep(reportGroup.reports[i]));

                // Check if the report group already exists in favoriteGroups
                const existingGroup = this.favoriteGroups.find(group => group.reportGroupID === reportGroup.reportGroupID);

                if (!existingGroup) {
                  // If not, add the report group and the first favorite report
                  this.favoriteGroups.push({
                    ...reportGroup,
                    reports: [_.cloneDeep(reportGroup.reports[i])]
                  });
                } else {
                  // If the group already exists, just add the favorite report to that group
                  existingGroup.reports.push(_.cloneDeep(reportGroup.reports[i]));
                }
              }
            }
          });
          this.favoriteReports.reports.sort(this.sortReportsByName);
          if (this.favoriteReports.reports.length == 0) {
            this.noFavoriteReports = true;
          }
          else {
            this.noFavoriteReports = false;
          }
          this.availableReports = data;
        }

        this.reportFavService.updateAvailableReports(this.availableReports);
        this.reportFavService.updateFavoriteReports(this.favoriteReports);
        this.reportFavService.setNoFavoriteReportsStatus(this.noFavoriteReports);
        this.reportFavService.updateTotalReports(this.totalReports);
        this.reportFavService.updateFavoriteGroups(this.favoriteGroups);
        this.isLoading = false;
      },
      error: err => {
        this.errorMessage = err.message;
        this.errorRetrievingReports = true;
        this.isLoading = false;
      }
    });
  }

  onQueryChange(event: any) {
    this.reportFavService.upddateQueryReport(this.filters.reportQuery);
  }

  private sortReportsByName(a: any, b: any) {
    var nameA = a.reportName.toUpperCase();
    var nameB = b.reportName.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  }

  private refreshData() {
    window.location.reload();
  }
}
